import {
   documentToReactComponents,
   type NodeRenderer,
   type Options,
   type RenderNode
} from "@contentful/rich-text-react-renderer";
import { BLOCKS, type Document, INLINES } from "@contentful/rich-text-types";
import { cn } from "@tine/designsystem-utils";

import type { ContentEntryType, EmbeddedEntryContextType } from "../../common/types/cmsTypes";

import contentStore from "../../stores/cms/contentStore";

import Divider from "../Divider";
import { DownloadLink, ExternalLink, InternalLink } from "../Links";
import Quote from "../Quote";
import CmsEmbeddedEntry from "./CmsEmbeddedEntry";
import { RichTextColumn, RichTextListItem, RichTextTable, type TextTags } from "./RichTextRendererHelpers";

type RichTextRendererProps = {
   document: Document;
   variant?: "simple" | "standard" | "informationBox";
   context: EmbeddedEntryContextType;
   trackingLocationName?: string;
};

const contentBlock: { [variant in "simple" | "standard" | "informationBox"]: string } = {
   simple: "my-8",
   standard: "my-16",
   informationBox: "my-0"
};

const RichTextRenderer = ({ document, variant = "standard", context, trackingLocationName }: RichTextRendererProps) => {
   const textNodeRenderer =
      (tag: TextTags): NodeRenderer =>
      (node, children) => {
         const isListTag = tag === "ul" || tag === "ol";
         const isTableTag = tag === "table";
         const isListItem = tag === "li";
         const isHorizontalRule = tag === "hr";
         const isBlockQuote = tag === "blockquote";

         const Tag = tag;
         const listElementStyle = cn("pl-6", { "[&>li]:list-decimal": tag === "ol", "[&>li]:list-disc": tag === "ul" });

         if (isListItem) {
            return <RichTextListItem node={node} customRenderNodes={assetAndEntryRenderNodes} />;
         }

         if (
            context === "category-content" &&
            tag === "p" &&
            (!children || (Array.isArray(children) && (children.length === 0 || children.every((child) => !child))))
         ) {
            // don't render empty paragraphs in category content
            return null;
         }

         return (
            <RichTextColumn>
               {isListTag && <Tag className={listElementStyle}>{children}</Tag>}
               {isTableTag && <RichTextTable table={node} customRenderNodes={assetAndEntryRenderNodes} />}
               {isHorizontalRule && <Divider />}
               {isBlockQuote && <Quote>{children}</Quote>}
               {!isListTag && !isTableTag && !isHorizontalRule && !isBlockQuote && <Tag>{children}</Tag>}
            </RichTextColumn>
         );
      };

   const textRenderNodes: RenderNode = {
      [BLOCKS.PARAGRAPH]: textNodeRenderer("p"),
      [BLOCKS.HEADING_2]: textNodeRenderer("h2"),
      [BLOCKS.HEADING_3]: textNodeRenderer("h3"),
      [BLOCKS.HEADING_4]: textNodeRenderer("h4"),
      [BLOCKS.HEADING_5]: textNodeRenderer("h5"),
      [BLOCKS.HEADING_6]: textNodeRenderer("h6"),
      [BLOCKS.UL_LIST]: textNodeRenderer("ul"),
      [BLOCKS.OL_LIST]: textNodeRenderer("ol"),
      [BLOCKS.TABLE]: textNodeRenderer("table"),
      [BLOCKS.HR]: textNodeRenderer("hr"),
      [BLOCKS.QUOTE]: textNodeRenderer("blockquote")
   };

   const assetAndEntryRenderNodes: RenderNode = {
      [BLOCKS.LIST_ITEM]: textNodeRenderer("li"),
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
         if (!node.data?.target?.fields) {
            return null;
         }
         const nodeId = node.data.target.sys.id;
         const nodeType = node.data.target.sys.contentType.sys.id as ContentEntryType;

         return (
            <div className={cn(contentBlock[variant], "first:mt-0 last:mb-0")}>
               <CmsEmbeddedEntry entryId={nodeId} content={node.data.target} type={nodeType} context={context} />
            </div>
         );
      },
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
         if (!node.data?.target?.fields?.file?.details?.image) {
            return null;
         }
         return (
            <div className={cn(contentBlock[variant], "max-w-4xl mx-auto space-y-2")}>
               <img
                  src={`https://${node.data.target.fields.file.url}`}
                  height={node.data.target.fields.file.details.image.height}
                  width={node.data.target.fields.file.details.image.width}
                  alt={node.data.target.fields.title}
                  className="size-full rounded-lg"
                  loading="lazy"
               />
               {node.data.target.fields.description && <p className="text-sm italic">{node.data.target.fields.description}</p>}
            </div>
         );
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
         const url = node.data.target.fields.file.url;
         return <DownloadLink href={url}>{children}</DownloadLink>;
      },
      [INLINES.HYPERLINK]: (node, children) => {
         const url = node.data.uri;
         if (url.startsWith("/")) {
            return <InternalLink href={url}>{children}</InternalLink>;
         }
         return <ExternalLink href={url}>{children}</ExternalLink>;
      },
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
         const nodeId = node.data.target.sys.id;
         const existingContent = contentStore.urlData.data.find((data) => data.id === nodeId);
         if (existingContent) {
            return (
               <InternalLink href={`/${existingContent.fullUrl}`} trackingLocationName={trackingLocationName}>
                  {children}
               </InternalLink>
            );
         }
         return <>{children}</>;
      }
   };

   const customRenderOptions: Options = {
      renderNode: {
         ...textRenderNodes,
         ...assetAndEntryRenderNodes
      }
   };
   return <>{documentToReactComponents(document, customRenderOptions)}</>;
};

export default RichTextRenderer;
